import classNames from 'classnames';
import React, {
    FC,
    memo,
    MouseEventHandler,
    MutableRefObject,
    PropsWithChildren,
    ReactNode,
    useEffect,
    useId,
    useMemo,
} from 'react';
import style from './style.module.scss';
import {ITooltip as IReactTooltip, PositionStrategy, Tooltip as ReactTooltip} from 'react-tooltip';
import {TPropsWithClassName} from 'core/types';
import {Typography} from 'components/Typography';

import {createPortal} from 'react-dom';

interface ITooltipPortalProps {
    children: ReactNode;
    body?: Element | null;
}

export const TooltipPortal: FC<ITooltipPortalProps> = ({
    children,
    body = 'undefined' !== typeof document ? document.body : null,
}) => {
    const [portalContainer, setPortalContainer] = React.useState<HTMLElement | null>(null);

    useEffect(() => {
        if (!body) {
            return;
        }

        let container = document.getElementById('tooltip-portal-container');
        if (!container) {
            container = document.createElement('div');
            container.id = 'tooltip-portal-container';
            container.style.position = 'fixed';
            container.style.top = '0';
            container.style.left = '0';
            container.style.width = '100%';
            container.style.height = '100%';
            container.style.pointerEvents = 'none';
            container.style.zIndex = '9999';
            body.appendChild(container);
        }
        setPortalContainer(container);
    }, [body]);

    if (!portalContainer) {
        return null;
    }

    return createPortal(children, portalContainer);
};

export const TOOLTIP_COLOR = {
    red: 'red',
    white: 'white',
};

export type TTooltipColor = keyof typeof TOOLTIP_COLOR;

export type TPlacesType =
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'left'
    | 'left-start'
    | 'left-end';

interface ITooltip extends IReactTooltip {
    childrenClassName?: string;
    overlay?: ReactNode;
    overlayClassName?: string;
    overlayRef?: MutableRefObject<HTMLDivElement | null>;
    color?: TTooltipColor;
    showArrow?: boolean;
    openOnClick?: boolean;
    isOpen?: boolean;
    onClick?: MouseEventHandler<HTMLDivElement>;
    afterShow?: () => void;
    afterHide?: () => void;
    setIsOpen?: (value: boolean) => void;
    text?: string;
    id?: string;
    hidden?: boolean;
    positionStrategy?: PositionStrategy;
}

export type TTooltip = PropsWithChildren<TPropsWithClassName<ITooltip>>;

export const Tooltip = memo<TTooltip>(function Tooltip({
    className,
    childrenClassName,
    overlay,
    overlayClassName,
    children,
    showArrow = true,
    openOnClick = false,
    setIsOpen,
    color = TOOLTIP_COLOR.white,
    place = 'bottom',
    isOpen,
    onClick,
    afterShow,
    afterHide,
    text,
    id,
    hidden,
    overlayRef,
    positionStrategy = 'fixed',
    ...rest
}) {
    const childRef = React.useRef<HTMLDivElement>(null);

    const innerId = useId();
    const tooltipId = id || innerId;

    const overlayBlock = useMemo(
        () =>
            Boolean(overlay) ? (
                overlay
            ) : (
                <div className={style.overlayBlock}>
                    <Typography className={'overlay-text'} variant={'p-small'}>
                        {text}
                    </Typography>
                </div>
            ),
        [overlay, text],
    );

    return (
        <>
            <TooltipPortal>
                <ReactTooltip
                    hidden={hidden}
                    afterHide={afterHide}
                    afterShow={afterShow}
                    className={classNames(style.customTooltip, style[color], className)}
                    clickable={true}
                    id={tooltipId}
                    isOpen={isOpen}
                    noArrow={!showArrow}
                    openOnClick={openOnClick}
                    place={place}
                    setIsOpen={setIsOpen}
                    positionStrategy={positionStrategy}
                    opacity={1}
                    {...rest}
                >
                    {overlayBlock}
                </ReactTooltip>
            </TooltipPortal>
            <div
                className={classNames(style.tooltipWrapper, childrenClassName)}
                data-tooltip-id={tooltipId}
                onClick={onClick}
                ref={childRef}
            >
                {children}
            </div>
        </>
    );
});
